import moment from 'moment';
import { create } from 'zustand';

import { api } from 'src/utils/axios';
import { jwtDecode } from 'src/utils/isValidToken';

import { translateTier } from 'src/_mock/user/points';
import { setAnalyticsUserId } from 'src/init-firebase';
import { useMissionsStore } from 'src/stores/missions/missions.store';
import { UserRepository } from 'src/repositories/auth/user.repository';
import { AchievementsRepository } from 'src/repositories/achievements/achievements.repository';

import {
  UserType,
  TermsType,
  WalletType,
  MemberCardType,
  UserPointsNotificationType,
} from 'src/types/user/userType';

import { useMyLevelStore } from './my-level.store';

const LOGIN_START_MISSION_ID = '69f0f8fb6f400d3963b0fe1f';

type States = {
  isLoading: boolean;
  isLoadingUserData: boolean;
  isLoadingSuggestion: boolean;
  isLoadingMyBalance: boolean;
  isLoadingDeleteAccount: boolean;
  isLoadingPaymentErrorCount: boolean;
  isLoadingPaymentFailure: boolean;
  isLoadingAccept: boolean;
  isLoadingUpdateUser: boolean;
  isLoadingMemberCard: boolean;
  isLoadingOnboarding: boolean;
  isFetchingLogin: boolean;
  isLoadingUserPointsNotifications: boolean;
  user?: UserType;
  terms?: TermsType;
  wallet?: WalletType;
  paymentErrorCount?: { errorCount: number };
  memberCard: MemberCardType | null;
  openSendNotes: boolean;
  newVivaHeader?: boolean;
  userPointsNotifications?: UserPointsNotificationType | null;
  isBenefitsBottomsheetOpen: boolean;
};

type loginParams = {
  code: string;
  setSession: (accessToken: string, refreshToken: string) => void;
};

type Actions = {
  setIsLoading: (loadingState: boolean) => void;
  setUser: (user: UserType) => void;
  login: (body: loginParams) => Promise<UserType | 'fetching'>;
  userData: () => Promise<void>;
  myBalance: () => Promise<void>;
  termsCheck: () => Promise<void>;
  termsAccept: () => Promise<void>;
  sendSuggestion: (suggestion: string, area: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  getPaymentErrorCount: () => Promise<void>;
  postPaymentError: () => Promise<void>;
  getDeeplinks: (page: string, origin: string) => Promise<{ page: string; redirect: string }>;
  updatePhoto: (url: string) => Promise<void>;
  updateUser: (user: { doubleVerification: boolean }) => Promise<void>;
  getMemberCard: (params?: any) => Promise<any>;
  confirmOnboarding: () => Promise<void>;
  setOpenSendNotes: (v: boolean, logAction: any) => void;
  getUserPointsNotifications: () => Promise<void>;
  setIsBenefitsBottomsheetOpen: (isOpen: boolean) => void;
};

type UserStore = Actions & States;

const initialValues = {
  isLoading: false,
  isLoadingUserData: false,
  isLoadingSuggestion: false,
  isLoadingMyBalance: false,
  isLoadingDeleteAccount: false,
  isLoadingPaymentErrorCount: false,
  isLoadingPaymentFailure: false,
  isLoadingAccept: false,
  isLoadingUpdateUser: false,
  isLoadingMemberCard: false,
  isLoadingOnboarding: false,
  isFetchingLogin: false,
  isLoadingUserPointsNotifications: false,
  user: undefined,
  terms: undefined,
  wallet: undefined,
  paymentErrorCount: undefined,
  memberCard: null,
  openSendNotes: false,
  newVivaHeader: false,
  userPointsNotifications: null,
  isBenefitsBottomsheetOpen: false,
};

const userRepository = new UserRepository(api);
const achievementsRepository = new AchievementsRepository(api);

export const useUserStore = create<UserStore>((set: any, get: any) => ({
  ...initialValues,

  setOpenSendNotes: async (v: boolean, logAction: any) => {
    const response = await userRepository.getConfig('INVOICES_MODAL');
    const myLevel = useMyLevelStore.getState();

    if (response.isLeft()) {
      set({ openSendNotes: v });
      return;
    }

    if (
      response?.value?.value &&
      response?.value?.value?.startDate &&
      moment(response?.value?.value?.startDate).startOf('day').isBefore(new Date()) &&
      response?.value?.value?.endDate &&
      moment(response?.value?.value?.endDate).endOf('day').isAfter(new Date())
    ) {
      const responseOptin = await userRepository.getPromotionOptin();

      if (responseOptin.isRight() && responseOptin?.value?.singlePromotionAndAccepted) {
        logAction('invoicesClick', 'prizor', '', ['roxcode', 'cn_firebase'], {
          log_name: 'viva_invoices_click_prizor',
          user_tier: translateTier[myLevel?.myLevelInfo?.userActualTier!],
          since_date: myLevel?.myLevelInfo?.actualLevel?.sinceDate,
          date: new Date().toLocaleString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
        });
        window.open(
          `superapp://viva/prizor/campanhas/${translateTier[myLevel?.myLevelInfo?.userActualTier!]}/${myLevel?.myLevelInfo?.actualLevel?.sinceDate}/home`,
          '_self'
        );
        return;
      }

      set({ openSendNotes: v });
      return;
    }

    logAction('invoicesClick', 'iris', '', ['roxcode', 'cn_firebase'], {
      log_name: 'viva_invoices_click_iris',
      date: new Date().toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    });
    window.open('superapp://viva/iris', '_self');
  },

  setIsLoading: (loadingState: boolean) => set({ isLoading: loadingState }),

  setUser: (userInfo: UserType) => set({ user: userInfo }),

  login: async ({ code, setSession }: loginParams) => {
    if (get().isFetchingLogin) return 'fetching';
    set({ isLoading: true, isFetchingLogin: true });

    try {
      const response = await userRepository.login({ code });

      if (response.isLeft()) {
        set({ isLoading: false, isFetchingLogin: false });
        throw new Error(response.value.message);
      }
      setSession(response.value.accessToken, response.value.refreshToken);
      set({ isLoading: false, isFetchingLogin: false });

      try {
        const header = await userRepository.getConfig('NEW_HEADER');
        if (header.isLeft()) {
          set({ newVivaHeader: false });
        } else {
          set({ newVivaHeader: header.value.value });
        }
      } catch (headerError) {
        console.error('Erro ao buscar o header:', headerError);
        set({ newVivaHeader: false });
      }

      let trailData = jwtDecode(response.value.accessToken)?.data?.trailData;

      if (!trailData.expiresAt || moment(trailData.expiresAt).isBefore(new Date())) {
        ({
          value: { trailData },
        } = await achievementsRepository.assignTrail());
      }

      const user = await get().userData(trailData);
      await useMissionsStore.getState().startCustomScanMission(LOGIN_START_MISSION_ID);
      get().myBalance();

      if (user?.userLevel !== 'diamond') {
        get().getUserPointsNotifications();
      }

      return user;
    } catch (e) {
      set({ isLoading: false });
      throw new Error(e.message);
    }
  },

  getUserPointsNotifications: async () => {
    set({ isLoadingUserPointsNotifications: true });

    try {
      const response = await userRepository.getUserPointsNotifications();

      if (response.isLeft()) {
        set({ isLoadingUserPointsNotifications: false });
        throw new Error(response.value.message);
      }

      set({ userPointsNotifications: response.value });
      set({ isLoadingUserPointsNotifications: false });
    } catch (error) {
      set({ isLoadingUserPointsNotifications: false });
      throw new Error(error.message);
    }
  },

  userData: async (trailData?: any) => {
    set({ isLoadingUserData: true });

    try {
      const response = await userRepository.userData();

      if (response.isLeft()) {
        set({ isLoadingUserData: false });
        throw new Error(response.value.message);
      }

      set({ user: { ...response.value, trailData: trailData || get().user?.trailData } });

      if (response.value?.cpf) {
        setAnalyticsUserId(response.value.cpf);
      }

      try {
        const header = await userRepository.getConfig('NEW_HEADER');
        if (header.isLeft()) {
          set({ newVivaHeader: false });
        } else {
          set({ newVivaHeader: header.value.value });
        }
      } catch (headerError) {
        console.error('Erro ao buscar o header:', headerError);
        set({ newVivaHeader: false });
      }

      set({ isLoadingUserData: false });
      return response.value;
    } catch (error) {
      set({ isLoadingUserData: false });
      throw new Error(error.message);
    }
  },

  myBalance: async () => {
    set({ isLoadingMyBalance: true });

    const response = await userRepository.myBalance();

    if (response.isLeft()) {
      set({ isLoadingMyBalance: false });
      throw new Error(response.value.message);
    }

    set({ wallet: response.value });
    set({ isLoadingMyBalance: false });
  },

  termsCheck: async () => {
    set({ isLoading: true });

    const response = await userRepository.termsCheck();

    if (response.isLeft()) {
      set({ isLoading: false });
      throw new Error(response.value.message);
    }

    set({ terms: response.value });
    set({ isLoading: false });
  },

  termsAccept: async () => {
    set({ isLoadingAccept: true });

    const response = await userRepository.termsAccept(get().terms?.terms?.version);

    if (response.isLeft()) {
      set({ isLoadingAccept: false });
      throw new Error(response.value.message);
    }

    try {
      await get().termsCheck();
    } catch {
      set({ isLoadingAccept: false });
      throw new Error(response.value.message);
    }
  },

  sendSuggestion: async (suggestion: string, area: string) => {
    set({ isLoadingSuggestion: true });

    const response = await userRepository.sendSuggestion({ suggestion, area });

    if (response.isLeft()) {
      set({ isLoadingSuggestion: false });
      throw new Error(response.value.message);
    }

    set({ isLoadingSuggestion: false });
  },

  deleteAccount: async () => {
    set({ isLoadingDeleteAccount: true });

    const response = await userRepository.deleteAccount();
    if (response.isLeft()) {
      set({ isLoadingDeleteAccount: false });
      throw new Error(response.value.message);
    }
    set({ isLoadingDeleteAccount: false });
  },

  getPaymentErrorCount: async () => {
    set({ isLoadingPaymentErrorCount: true });

    const response = await userRepository.getPaymentErrorCount();
    if (response.isLeft()) {
      set({ isLoadingPaymentErrorCount: false });
      throw new Error(response.value.message);
    }

    set({ paymentErrorCount: response.value });
    set({ isLoadingPaymentErrorCount: false });
  },

  postPaymentError: async () => {
    set({ isLoadingPaymentFailure: true });

    const response = await userRepository.postPaymentError();
    if (response.isLeft()) {
      set({ isLoadingPaymentFailure: false });
      throw new Error(response.value.message);
    }

    set({ isLoadingPaymentFailure: false });
  },

  getDeeplinks: async (page: string, origin: string) => {
    const response = await userRepository.getDeeplinks({ page, origin });
    if (response.isLeft()) {
      throw new Error(response.value.message);
    }

    return response.value;
  },

  updatePhoto: async (url: string) => {
    const response = await userRepository.updatePhoto(url);
    if (response.isLeft()) {
      throw new Error(response.value.message);
    }
  },

  updateUser: async (user: { doubleVerification: boolean }) => {
    set({ isLoadingUpdateUser: true });
    const response = await userRepository.updateUser(user);
    if (response.isLeft()) {
      set({ isLoadingUpdateUser: false });
      throw new Error(response.value.message);
    }
    await get().userData();
    set({ isLoadingUpdateUser: false });
  },

  getMemberCard: async (params?: any) => {
    set({ isLoadingMemberCard: true });
    const response = await userRepository.getMemberCard(params);
    if (response.isLeft()) {
      set({ memberCard: null, isLoadingMemberCard: false });
      return null;
    }

    set({ memberCard: response.value });
    set({ isLoadingMemberCard: false });
    return response.value;
  },

  confirmOnboarding: async () => {
    set({ isLoadingOnboarding: true });
    const response = await userRepository.confirmOnboarding();
    if (response.isLeft()) {
      set({ isLoadingOnboarding: false });
      throw new Error(response.value.message);
    }

    set({ isLoadingOnboarding: false });
  },

  setIsBenefitsBottomsheetOpen: (isOpen: boolean) => set({ isBenefitsBottomsheetOpen: isOpen }),
}));
